import { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { storageSearch, storageUpload, storageMeta, tagManagerPersist, updatePersonAttachmentCount } from "../../api";
import { AttachmentModal } from "./attachmentmodal";
import { AttachmentRow } from "./attachmentrow";
import { jsError } from "../utils";
import { Button } from "../Button.tsx";

const MAX_FILESIZE_MB = 20;

const mapTagsToCodeKeys = (tags) => tags.map((tag) => ({ codeKey: tag }));

// savedId should get the database id of the entity when it has been created/updated
export function AttachmentView({ section, entityid, attachments, savedId, readOnly, isPersonal, onListChange }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (savedId && !uploading) {
      uploadHandler();
      setUploading(true);
    }
  }, [savedId]);

  async function uploadHandler() {
    const collectedTags = [];
    const promises = [];
    for (const item of list) {
      if (!item.key) {
        // new attachment (upload)
        if (item.file.size > MAX_FILESIZE_MB * 1048576) {
          alert(
            t("FileTooLarge", "Tiedosto {{fileName}} on liian suuri", { fileName: item.file.name }), // original filename
          );
        } else {
          // TODO: send all metadata in a single base64url-encoded json-stringified header
          const meta = {
            // metadata is sent in request headers
            section,
            entityid: savedId, // id of the entity saved before attachement upload
            filename: encodeURIComponent(item.filename),
            lastmodified: item.file.lastModified,
            "Content-Type": item.file.type,
            tags: encodeURIComponent(JSON.stringify(item.tags)),
          };

          if (item.description !== null && item.description !== undefined) meta.description = item.description;
          if (item.type !== null && item.type !== undefined) meta.type = item.type;

          // add progress bar
          const uploadItem = { ...item, progress: 0 };
          setList((currentList) => currentList.map((currentItem) => (currentItem === item ? uploadItem : currentItem)));

          const onUploadProgress = (progressEvent) => {
            uploadItem.progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setList((currentList) => [...currentList]); // update progress bar
          };

          collectedTags.push(...mapTagsToCodeKeys(item.tags));
          promises.push(storageUpload(meta, item.file, onUploadProgress));
        }
      } else if (item.changed) {
        // existing attachment (change metadata)
        const { key, filename, type, description, tags } = item;
        const newData = {
          key,
          filename,
          type,
          description,
          tags,
          section,
          entityid: savedId,
        };
        try {
          tagManagerPersist(mapTagsToCodeKeys(tags));
          await storageMeta(newData);
        } catch (err) {
          jsError(err);
          alert(`${filename}: ${err.message}`);
        }
      }
    }

    try {
      if (collectedTags.length) {
        tagManagerPersist(collectedTags);
      }
      await Promise.all(promises); // wait for all uploads to finish before updating the list and person attachment count
      if (section === "person") {
        const counts = [];
        for (const promise of promises) {
          const { data } = await promise;
          counts.push(Number(data.count)); // upload returns the current count of entityid attachments
        }
        if (counts.length) {
          updatePersonAttachmentCount({
            personId: entityid,
            otherDetails: {
              attachmentCount: Math.max(...counts),
            },
          });
        }
      }
    } catch (err) {
      jsError(err);
      alert(`Error in upload: ${err.message}`);
      //alert(`${uploadItem.filename}: ${err.message}`);
    }

    dispatch({ type: "UPLOAD_DONE" });
  } // uploadHandler()

  // display newly added files as AttachmentRow items to allow metadata edits
  const newFiles = (files) =>
    [...files].map((file) => ({
      // destructure a FileList object into an array
      file, // uploadable File object with properties: name, size, type, lastModified
      key: null,
      filename: file.name,
      created: Date.now(),
      type: null,
      description: null,
      tags: [],
    }));

  const selectFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = () => setList([...newFiles(input.files), ...list]);
    input.click();
  };

  let dragCounter = 0;
  const dragClass = "dragOutline";
  const dragOver = (e) => e.preventDefault();
  const dragEnter = (e) => {
    if (dragCounter === 0) e.currentTarget.classList.add(dragClass);
    ++dragCounter;
  };
  const dragLeave = (e) => {
    if (--dragCounter === 0) {
      e.currentTarget.classList.remove(dragClass);
    }
  };

  /*
  useEffect(() => {
    console.info("Mount");
    window.ondragover = dragOver;
    window.ondragenter = dragEnter;
    window.ondragleave = dragLeave;
    window.ondrop = drop;
    return () => {
      console.info("Unmount");
      window.ondragover = null;
      window.ondragenter = null;
      window.ondragleave = null;
      window.ondrop = null;
    };
  }, []);
*/

  // when an empty array is returned, the end of the directory has been reached, and the recursion ends
  // if no recursion is used, readEntries returns only 100 first entries
  const readDirectory = (directory) =>
    new Promise((resolve, reject) => {
      const directoryReader = directory.createReader();
      const entries = [];

      const getEntries = () => {
        directoryReader.readEntries(
          (results) => {
            if (results.length) {
              entries.push(...results);
              getEntries();
            } else resolve(entries);
          },
          (error) => reject(error),
        );
      };

      getEntries();
    });

  // recursively process FileEntry or DirectoryEntry objects
  async function processEntry(entry, files) {
    if (entry.isFile) {
      entry.file(
        (f) => {
          files.push(f);
          if (--files.reading === 0) setList([...newFiles(files), ...list]);
        },
        (err) => jsError("file error:", err),
      );
    } else if (entry.isDirectory) {
      const entries = await readDirectory(entry);
      files.reading += entries.length;
      for (const entry of entries) processEntry(entry, files);
      if (--files.reading === 0) setList([...newFiles(files), ...list]);
    }
  }

  const drop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove(dragClass);
    dragCounter = 0;

    const files = [];
    files.reading = e.dataTransfer.items.length;
    for (const item of e.dataTransfer.items) {
      const entry = item.getAsEntry !== undefined ? item.getAsEntry() : item.webkitGetAsEntry();
      processEntry(entry, files);
    }
  };

  const [list, setList] = useState([]);
  useEffect(() => {
    if (attachments && attachments.result) {
      setList(attachments.result);
    }
  }, [attachments]);

  async function search() {
    const query = {
      section,
      entityid,
    };
    try {
      const res = await storageSearch(query);
      if (res.status === 200) {
        setList(res.data.result);
      }
    } catch (error) {
      jsError(error.message);
    }
  }

  const [displayAttachment, setDisplayAttachment] = useState(null);

  function onCancel() {
    setDisplayAttachment(null);
  }

  function open(data) {
    const meta = {
      data,
      key: data.key,
      section,
      entityid,
    };
    setDisplayAttachment(meta);
  }

  function changeData(action) {
      if (action.changeIdx !== undefined) {
        // modify a single property of an attachment
        const newList = list.map((item, idx) => {
          if (idx === action.changeIdx) {
            return { ...item, ...action.payload, changed: true }; // mark metadata as changed
          } else return item;
        });
        setList(newList);
        if (typeof onListChange === "function") onListChange(newList);
      } else if (action.removeIdx !== undefined) {
        const newList = list.filter((item, idx) => idx !== action.removeIdx);
        setList(newList);
        if (typeof onListChange === "function") onListChange(newList);
      }
    }

  return (
    <>
      <AttachmentModal attachment={displayAttachment} onCancel={onCancel} />
      <div
        className="uploadForm"
        onDragOver={dragOver}
        onDragEnter={dragEnter}
        onDragLeave={dragLeave}
        onDrop={readOnly ? null : drop}
      >
        {!readOnly && !isPersonal && (
          <>
            <p>
              {t(
                "DragFiles",
                "Vedä tiedostot tähän tai avaa valintaikkuna alapuolelta. Suurin sallittu tiedostokoko on {{maxSize}}M.",
                { maxSize: MAX_FILESIZE_MB },
              )}
            </p>
            <Button testId="select-files" variant="primary" onClick={selectFiles}>
              {t("SelectFiles", "Valitse tiedostot")}
            </Button>
          </>
        )}
        {list.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("Name", "Nimi")}</th>
                <th>{t("Date", "Päiväys")}</th>
                <th>{t("Category", "Kategoria")}</th>
                <th>{t("Description", "Kuvaus")}</th>
                <th>{t("Tags", "Tunnisteet")}</th>
                {!readOnly && <th>{t("Actions", "Toiminnot")}</th>}
              </tr>
            </thead>
            <tbody>
              {list.map((item, idx) => {
                const itemCopy = {
                  ...item,
                  section,
                  entityid,
                  idx,
                };
                return (
                  <AttachmentRow
                    key={item.key || idx}
                    data={itemCopy}
                    changeData={changeData}
                    open={open}
                    search={search}
                    section={section}
                    readOnly={readOnly}
                    isPersonal={isPersonal}
                  />
                );
              })}
            </tbody>
          </Table>
        )}
      </div>
    </>
  );
}
